/**
 * Floating launcher for Komunikimi (Bisedat), bottom-right.
 *
 * Chat owns this slot because it is opened far more often than support. Support
 * did not lose anything: its full panel now opens from the "Ndihmë" entry at the
 * bottom of the sidebar, and its own launcher still appears on the login page,
 * where there is no sidebar.
 *
 * Reuses `.nx-support-fab` so both launchers share one set of position, size and
 * motion rules — whichever module owns the slot, there is only one button to
 * maintain.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Badge, Tooltip } from '@mui/material';
import { chatOverview } from '../../services/chatApi';
import globals from '../../services/globals';
import ChatPanel from './ChatPanel';
import '../support/SupportWidget.css';

/** How often the unread count refreshes while the app is open. */
const POLL_MS = 60_000;

/**
 * Is there a session? This MUST be checked before calling chatOverview().
 *
 * chatApi goes through authFetch, and authFetch answers a 401 by clearing
 * storage and doing window.location.replace('/?logout=1'). This component lives
 * outside the <Switch>, so it is alive on the login page too — calling the API
 * there produced a 401 → redirect → remount → 401 … reload loop that stopped
 * the app from ever finishing startup. A try/catch does not help: the redirect
 * happens inside authFetch, not in the rejection.
 */
function hasSession() {
  try {
    const user = globals().getLogedInUser && globals().getLogedInUser();
    return Boolean(user && user.token);
  } catch { return false; }
}

export default function ChatFab() {
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  // Re-evaluated on every navigation, so the launcher appears right after login
  // without needing a reload.
  const signedIn = hasSession();

  const refresh = useCallback(async () => {
    if (!hasSession()) return;
    // A missing count is better than a wrong one — leave it as it was.
    try { setUnread((await chatOverview()).unread || 0); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!signedIn) { setUnread(0); return undefined; }
    if (open) return undefined;     // the panel refreshes it while it is open
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [refresh, signedIn, open, location.pathname]);

  // Signed out — nothing to launch, nothing may touch the chat API, and the
  // slot belongs to the support launcher instead.
  if (!signedIn) return null;

  // Already on the full chat page — a floating copy of it is clutter.
  if (location.pathname.startsWith('/communication')) return null;

  const label = unread > 0 ? `${unread} mesazh(e) të palexuara` : 'Bisedat';

  return (
    <>
      <ChatPanel open={open} onClose={() => setOpen(false)} onUnreadChange={setUnread} />
      <Tooltip title={label} placement="left" arrow>
      <button
        type="button"
        className="nx-support-fab"
        onClick={() => setOpen((v) => !v)}
        aria-label={label}
      >
        <Badge
          badgeContent={unread}
          color="error"
          max={99}
          overlap="circular"
          sx={{ '& .MuiBadge-badge': { fontWeight: 700, fontSize: '0.65rem' } }}
        >
          <svg className="nx-fab-chat" width="27" height="27" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="currentColor"
              d="M20 2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4v3.5a.5.5 0 0 0 .82.38L13.5 18H20a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Z"
            />
          </svg>
        </Badge>
      </button>
      </Tooltip>
    </>
  );
}
